import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../common/AuthProvider';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { IoMdEye, IoMdEyeOff } from 'react-icons/io';
import { RxCross2 } from 'react-icons/rx';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const Login = ({ closeModal }) => {
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [identifierType, setIdentifierType] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsGoogleLoading(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/google-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: credentialResponse.credential,
          refresh_token: credentialResponse.credential,
          provider: 'google',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Google authentication failed');
      }

      const data = await response.json();
      await login(data);
      if (closeModal) closeModal();
      navigate('/check-in/options');
    } catch (error) {
      console.error('Google login error:', error);
      toast.error(error.message || 'Google login failed');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) {
      toast.error('Please enter email or phone number');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/login-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: identifier.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send OTP');
      }

      const data = await response.json();
      setIdentifierType(data.identifier_type);
      setShowOtpInput(true);
      toast.success(`OTP sent to your ${data.identifier_type}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      toast.error('Please enter OTP');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/verify-login-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: identifier.trim(),
          otp: otp.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'OTP verification failed');
      }

      const data = await response.json();
      await login(data);
      if (closeModal) closeModal();
      navigate('/check-in/options');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/login-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: identifier.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to resend OTP');
      }

      toast.success(`OTP resent to your ${identifierType}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Full blank white screen during Google Auth */}
      {isGoogleLoading && <div className="fixed inset-0 bg-white z-50"></div>}

      <div className="fixed inset-0 flex justify-center items-center z-40">
        <ToastContainer position="top-center" autoClose={5000} />
        <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
        <div className="w-full max-w-md bg-white p-9 rounded-lg relative z-10 mx-2 shadow-lg">
          <button
            onClick={closeModal}
            className="absolute top-3 right-3 p-1 rounded-full text-lg text-gray-600 hover:text-black hover:ring-2 hover:ring-black transition-all duration-200 ease-in-out transform hover:scale-110 focus:outline-none"
            disabled={isLoading || isGoogleLoading}
            aria-label="Close modal"
          >
            <RxCross2 size={20} />
          </button>

          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900">Sign In</h2>
            <p className="text-gray-600 text-sm mt-1">
              Enter your email or phone number to receive OTP
            </p>
          </div>

          <div className="space-y-4">
            {/* Email/Phone Input */}
            <div>
              <label htmlFor="identifier" className="block text-xs font-medium text-gray-700 mb-1">
                Email or Phone Number
              </label>
              <input
                type="text"
                id="identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Enter your email or phone number"
                required
                disabled={isLoading || isGoogleLoading || showOtpInput}
              />
            </div>

            {/* Send OTP Button - Always visible when OTP input is not shown */}
            {!showOtpInput && (
              <button
                onClick={handleRequestOtp}
                className={`w-full flex justify-center py-2 px-4 text-sm font-medium text-white bg-black hover:bg-gray-800 rounded-md shadow-sm ${
                  isLoading || isGoogleLoading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
                disabled={isLoading || isGoogleLoading}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending OTP...
                  </>
                ) : (
                  'Send OTP'
                )}
              </button>
            )}

            {/* OTP Input Section - Appears after Send OTP is clicked */}
            {showOtpInput && (
              <>
                <div>
                  <label htmlFor="otp" className="block text-xs font-medium text-gray-700 mb-1">
                    Enter OTP
                  </label>
                  <input
                    type="text"
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    required
                    disabled={isLoading || isGoogleLoading}
                  />
                  <div className="flex justify-end items-center mt-1">
                    {/* <p className="text-xs text-gray-500">
                      OTP sent to: {identifier}
                    </p> */}
                    <button
                      type="button"
                      onClick={() => {
                        setShowOtpInput(false);
                        setOtp('');
                      }}
                      className="text-xs text-blue-600 hover:text-blue-500"
                      disabled={isLoading || isGoogleLoading}
                    >
                      {identifierType === 'phone' ? 'Use Email Instead' : 'Use Phone Instead'}
                    </button>
                  </div>
                </div>

                {/* Verify Button */}
                <button
                  onClick={handleVerifyOtp}
                  className={`w-full flex justify-center py-2 px-4 text-sm font-medium text-white bg-black hover:bg-gray-800 rounded-md shadow-sm ${
                    isLoading || isGoogleLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  disabled={isLoading || isGoogleLoading}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </button>

                {/* Resend OTP Link */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={isLoading || isGoogleLoading}
                    className="text-xs text-blue-600 hover:text-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Resend OTP
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Uncomment if you want to enable Google Sign-in */}
          {/* <div className="mt-4">
            <div className="relative flex items-center justify-center text-xs text-gray-500">
              <div className="w-full border-t border-gray-300"></div>
              <span className="absolute px-2 bg-white">Or continue with</span>
            </div>

            <div className="mt-6 w-full flex justify-center">
              <div className="w-full sm:w-96 md:w-[420px]">
                <GoogleOAuthProvider
                  clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
                >
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => {
                      toast.error("Google login failed");
                      setIsGoogleLoading(false);
                    }}
                    text="continue_with"
                    theme="filled_black"
                    shape="rectangular"
                    size="medium"
                    width={400}
                  />
                </GoogleOAuthProvider>
              </div>
            </div>
          </div> */}

          <div className="mt-4 text-center text-xs text-gray-600">
            Don't have an account?{' '}
            <a href="/create-user" className="text-blue-600 hover:text-blue-500 font-medium">
              Sign up
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;

// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { useAuth } from "../common/AuthProvider";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { IoMdEye, IoMdEyeOff } from "react-icons/io";
// import { RxCross2 } from "react-icons/rx";
// import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const Login = ({ closeModal }) => {
//   const [email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPassword, setShowPassword] = useState(false);
//   const [isLoading, setIsLoading] = useState(false);
//   const [isGoogleLoading, setIsGoogleLoading] = useState(false);
//   const { login } = useAuth();
//   const navigate = useNavigate();

//   const handleGoogleSuccess = async (credentialResponse) => {
//     setIsGoogleLoading(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/google-auth`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({
//           access_token: credentialResponse.credential,
//           refresh_token: credentialResponse.credential,
//           provider: "google",
//         }),
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || "Google authentication failed");
//       }

//       const data = await response.json();
//       await login(data);
//       if (closeModal) closeModal();
//       navigate("/check-in/options");
//     } catch (error) {
//       console.error("Google login error:", error);
//       toast.error(error.message || "Google login failed");
//     } finally {
//       setIsGoogleLoading(false);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setIsLoading(true);

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/guestlogin`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/x-www-form-urlencoded",
//         },
//         body: new URLSearchParams({
//           username: email,
//           password: password,
//         }),
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         if (errorData.detail === "Please verify your account first") {
//           toast.error("Please verify your account first");
//           setTimeout(() => {
//             navigate("/notcorrectotps", { state: { email } });
//           }, 2000);
//           return;
//         }
//         throw new Error(errorData.detail || "Login failed");
//       }

//       const data = await response.json();
//       await login(data);
//       navigate("/check-in/options");
//       if (closeModal) closeModal();
//     } catch (err) {
//       toast.error(err.message);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const togglePasswordVisibility = () => {
//     setShowPassword(!showPassword);
//   };

//   return (
//     <>
//       {/* Full blank white screen during Google Auth */}
//       {isGoogleLoading && <div className="fixed inset-0 bg-white z-50"></div>}

//       <div className="fixed inset-0 flex justify-center items-center z-40">
//         <ToastContainer position="top-center" autoClose={5000} />
//         <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
//         <div className="w-full max-w-md bg-white p-9 rounded-lg relative z-10 mx-2 shadow-lg">
//           <button
//             onClick={closeModal}
//             className="absolute top-3 right-3 p-1 rounded-full text-lg text-gray-600 hover:text-black hover:ring-2 hover:ring-black transition-all duration-200 ease-in-out transform hover:scale-110 focus:outline-none"
//             disabled={isLoading || isGoogleLoading}
//             aria-label="Close modal"
//           >
//             <RxCross2 size={20} />
//           </button>

//           <div className="text-center mb-6">
//             <h2 className="text-3xl font-bold text-gray-900">Sign In</h2>
//             <p className="text-gray-600 text-sm mt-1">
//               Please enter your email and password to sign in to your account
//             </p>
//           </div>

//           <form onSubmit={handleSubmit} className="space-y-4">
//             <div>
//               <label
//                 htmlFor="email"
//                 className="block text-xs font-medium text-gray-700 mb-1"
//               >
//                 Email
//               </label>
//               <input
//                 type="email"
//                 id="email"
//                 value={email}
//                 onChange={(e) => setEmail(e.target.value)}
//                 className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
//                 placeholder="Enter your email"
//                 required
//                 disabled={isLoading || isGoogleLoading}
//               />
//             </div>

//             <div>
//               <label
//                 htmlFor="password"
//                 className="block text-xs font-medium text-gray-700 mb-1"
//               >
//                 Password
//               </label>
//               <div className="relative">
//                 <input
//                   type={showPassword ? "text" : "password"}
//                   id="password"
//                   value={password}
//                   onChange={(e) => setPassword(e.target.value)}
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10 text-sm"
//                   placeholder="Enter your password"
//                   required
//                   disabled={isLoading || isGoogleLoading}
//                 />
//                 <button
//                   type="button"
//                   onClick={togglePasswordVisibility}
//                   className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-700"
//                   disabled={isLoading || isGoogleLoading}
//                   aria-label={showPassword ? "Hide password" : "Show password"}
//                 >
//                   {showPassword ? (
//                     <IoMdEyeOff size={16} />
//                   ) : (
//                     <IoMdEye size={16} />
//                   )}
//                 </button>
//               </div>
//             </div>

//             <div className="flex items-center justify-end">
//               <a
//                 href="/request-reset"
//                 className="text-xs text-blue-600 hover:text-blue-500"
//               >
//                 Forgot password?
//               </a>
//             </div>

//             <button
//               type="submit"
//               className={`w-full flex justify-center py-2 px-4 text-sm font-medium text-white bg-black hover:bg-gray-800 rounded-md shadow-sm ${
//                 isLoading || isGoogleLoading
//                   ? "opacity-70 cursor-not-allowed"
//                   : ""
//               }`}
//               disabled={isLoading || isGoogleLoading}
//             >
//               {isLoading ? (
//                 <>
//                   <svg
//                     className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                     xmlns="http://www.w3.org/2000/svg"
//                     fill="none"
//                     viewBox="0 0 24 24"
//                   >
//                     <circle
//                       className="opacity-25"
//                       cx="12"
//                       cy="12"
//                       r="10"
//                       stroke="currentColor"
//                       strokeWidth="4"
//                     ></circle>
//                     <path
//                       className="opacity-75"
//                       fill="currentColor"
//                       d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                     ></path>
//                   </svg>
//                   Logging in...
//                 </>
//               ) : (
//                 "Log in"
//               )}
//             </button>
//           </form>

//           {/* <div className="mt-4">
//             <div className="relative flex items-center justify-center text-xs text-gray-500">
//               <div className="w-full border-t border-gray-300"></div>
//               <span className="absolute px-2 bg-white">Or continue with</span>
//             </div>

//             <div className="mt-6 w-full flex justify-center">
//               <div className="w-full sm:w-96 md:w-[420px]">
//                 <GoogleOAuthProvider
//                   clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
//                 >
//                   <GoogleLogin
//                     onSuccess={handleGoogleSuccess}
//                     onError={() => {
//                       toast.error("Google login failed");
//                       setIsGoogleLoading(false);
//                     }}
//                     text="continue_with"
//                     theme="filled_black"
//                     shape="rectangular"
//                     size="medium"
//                     width={400} // ✅ makes it responsive
//                   />
//                 </GoogleOAuthProvider>
//               </div>
//             </div>

//             <div className="mt-4 text-center text-xs text-gray-600">
//               Don't have an account?{" "}
//               <a
//                 href="/create-user"
//                 className="text-blue-600 hover:text-blue-500 font-medium"
//               >
//                 Sign up
//               </a>
//             </div>
//           </div> */}
//           <div className="mt-4 text-center text-xs text-gray-600">
//               Don't have an account?{" "}
//               <a
//                 href="/create-user"
//                 className="text-blue-600 hover:text-blue-500 font-medium"
//               >
//                 Sign up
//               </a>
//             </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default Login;
